import { Col, Input, Row, Select } from "antd"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

/**
 * Reusable session form component for create and edit modals
 *
 * @param {Object} props
 * @param {'create' | 'edit'} props.mode - Form mode
 * @param {Object} props.data - Form data
 * @param {Function} props.onChange - Callback when field changes (field, value) => void
 * @param {boolean} props.showOperationMetrics - Whether to show operation metric fields
 * @param {Array} props.brandOptions - Brand dropdown options [{value, label}]
 * @param {Array} props.sourceOptions - Source dropdown options [{value, label}]
 * @param {boolean} props.disabled - Disable all fields (for view mode)
 * @param {Object} props.datetimeErrors - Datetime validation errors
 */
const SessionForm = ({
    mode = 'create',
    data = {},
    onChange,
    showOperationMetrics = false,
    brandOptions = [],
    sourceOptions = [],
    caseStatusOptions = [],
    disabled = false,
    datetimeErrors = {}
}) => {
    const handleChange = (field) => (eventOrValue) => {
        // Handle both event objects and direct values
        const value = eventOrValue?.target?.value !== undefined
            ? eventOrValue.target.value
            : eventOrValue
        onChange(field, value)
    }

    return (
        <div>
            {/* Basic Fields */}
            <Row style={{ marginTop: '15px' }}>
                แบรนด์:
                <div style={{ marginRight: '15px' }}>
                    <Select
                        style={{ width: '120px' }}
                        options={brandOptions}
                        value={data.brand}
                        onChange={handleChange('brand')}
                        disabled={disabled}
                    />
                </div>
                ช่องทางติดต่อ:
                <div>
                    <Select
                        style={{ width: '120px' }}
                        options={sourceOptions}
                        value={data.source}
                        onChange={handleChange('source')}
                        disabled={disabled}
                    />
                </div>
            </Row>
            <Row style={{ marginTop: '15px' }}>
                ชื่อ Social หรือ ชื่อผู้ติดต่อ:
                <Input
                    value={data.user_name}
                    onChange={handleChange('user_name')}
                    disabled={disabled}
                />
            </Row>

            {/* Operation Metric Fields - Only visible when showOperationMetrics is true */}
            {showOperationMetrics && (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Row style={{ marginTop: '15px', alignItems: 'center' }}>
                        <Col span={24}>
                            <div>เวลาแชทเข้า:</div>
                            <Row gutter={8} style={{ marginTop: '8px' }}>
                                <Col span={12}>
                                    <DatePicker
                                        value={data.chat_started_at_date}
                                        onChange={handleChange('chat_started_at_date')}
                                        format="DD/MM/YYYY"
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกวันที่'
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <TimePicker
                                        value={data.chat_started_at_time}
                                        onChange={handleChange('chat_started_at_time')}
                                        ampm={false}
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกเวลา'
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    <Row style={{ marginTop: '15px', alignItems: 'center' }}>
                        <Col span={24}>
                            <div>เวลาตอบแชทครั้งแรก:</div>
                            <Row gutter={8} style={{ marginTop: '8px' }}>
                                <Col span={12}>
                                    <DatePicker
                                        value={data.chat_first_response_at_date}
                                        onChange={handleChange('chat_first_response_at_date')}
                                        format="DD/MM/YYYY"
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกวันที่'
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <TimePicker
                                        value={data.chat_first_response_at_time}
                                        onChange={handleChange('chat_first_response_at_time')}
                                        ampm={false}
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกเวลา'
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                            {datetimeErrors?.chat_first_response_at && (
                                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {datetimeErrors.chat_first_response_at}
                                </div>
                            )}
                        </Col>
                    </Row>

                    <Row style={{ marginTop: '15px', alignItems: 'center' }}>
                        <Col span={24}>
                            <div>เวลาจบแชท:</div>
                            <Row gutter={8} style={{ marginTop: '8px' }}>
                                <Col span={12}>
                                    <DatePicker
                                        value={data.chat_ended_at_date}
                                        onChange={handleChange('chat_ended_at_date')}
                                        format="DD/MM/YYYY"
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกวันที่'
                                            }
                                        }}
                                    />
                                </Col>
                                <Col span={12}>
                                    <TimePicker
                                        value={data.chat_ended_at_time}
                                        onChange={handleChange('chat_ended_at_time')}
                                        ampm={false}
                                        disabled={disabled}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                placeholder: 'เลือกเวลา'
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                            {datetimeErrors?.chat_ended_at && (
                                <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {datetimeErrors.chat_ended_at}
                                </div>
                            )}
                        </Col>
                    </Row>

                    <Row style={{ marginTop: '15px' }}>
                        <Col span={24}>
                            <div>สถานะเคส:</div>
                            <Select
                                style={{ width: '100%', marginTop: '8px' }}
                                options={caseStatusOptions}
                                value={data.case_status}
                                onChange={handleChange('case_status')}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                </LocalizationProvider>
            )}
        </div>
    )
}

export default SessionForm
